import React, { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { callApi } from "../../api/api";
import {
  ArrowLeft,
  Calendar,
  Layers,
  Edit,
  Download,
  FileText,
  ExternalLink,
  ChevronDown,
  Check,
} from "lucide-react";

const THEME = "rgb(37, 99, 235)";
const THEME_LIGHT = "rgba(37, 99, 235, 0.08)";

/* ─────────────────────────────────────────────────────────────
   SEARCHABLE SELECT COMPONENT
───────────────────────────────────────────────────────────── */
function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  disabled = false,
  loading = false,
  showSearch = false,
  id,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 200, openUpward: false });
  const [inputValue, setInputValue] = useState("");

  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) {
      setInputValue(selected ? selected.label || selected.value : value && value !== "all" ? value : "");
    }
  }, [value, selected, open]);

  const updatePosition = () => {
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < 240 && rect.top > spaceBelow;
      const width = Math.max(220, rect.width);

      let left = rect.left;
      if (left + width > window.innerWidth - 10) {
        left = window.innerWidth - width - 10;
      }
      if (left < 10) left = 10;

      let top = openUpward ? rect.top - 4 : rect.bottom + 4;
      setCoords({ top, left, width, openUpward });
    }
  };

  const checkPositionAndOpen = () => {
    if (disabled || loading) return;
    updatePosition();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handleScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", handleMouseDown);
    }
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  const filtered = options.filter((o) => {
    if (!showSearch || !inputValue.trim() || (selected && inputValue === (selected.label || selected.value))) return true;
    const q = inputValue.toLowerCase();
    return (
      (o.label || "").toLowerCase().includes(q) ||
      (o.value || "").toLowerCase().includes(q) ||
      (o.sub || "").toLowerCase().includes(q)
    );
  });

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }} id={id}>
      <div
        onClick={checkPositionAndOpen}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "7px 10px",
          border: open ? `1px solid ${THEME}` : "1px solid #cbd5e1",
          borderRadius: "7px",
          background: disabled ? "#f8fafc" : "#ffffff",
          cursor: disabled ? "not-allowed" : "pointer",
          fontSize: "12.5px",
          color: "#0f172a",
          height: "34px",
          boxSizing: "border-box",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={14} color="#64748b" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }} />
      </div>

      {open && (
        <div
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            width: coords.width,
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            borderRadius: "8px",
            boxShadow: "0 10px 25px -5px rgba(0,0,0,0.15)",
            zIndex: 99999,
            maxHeight: "220px",
            overflowY: "auto",
            padding: "4px",
            boxSizing: "border-box",
          }}
        >
          {showSearch && (
            <div style={{ padding: "4px 6px 6px" }}>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Search..."
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "12px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          )}
          {filtered.length === 0 ? (
            <div style={{ padding: "8px", fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>No options</div>
          ) : (
            filtered.map((opt, idx) => {
              const isSel = opt.value === value;
              return (
                <div
                  key={opt.value || idx}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  style={{
                    padding: "7px 10px",
                    borderRadius: "5px",
                    background: isSel ? THEME_LIGHT : "transparent",
                    color: isSel ? THEME : "#1e293b",
                    fontSize: "12px",
                    fontWeight: isSel ? "700" : "500",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <div>{opt.label}</div>
                    {opt.sub && <div style={{ fontSize: "10.5px", color: "#64748b" }}>{opt.sub}</div>}
                  </div>
                  {isSel && <Check size={13} color={THEME} />}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   CLASSIC ASSIGNMENT DETAILS COMPONENT
───────────────────────────────────────────────────────────── */
export default function ClassicAssignmentDetails({ item, onBack, onSaved, isOnlyTeamMember = false }) {
  const { instance, accounts } = useMsal();

  const [activeTab, setActiveTab] = useState("details");
  const [activeEdits, setActiveEdits] = useState({
    header: false,
    state: false,
    assignee: false,
    details: false,
  });

  const [editForm, setEditForm] = useState({
    title: "",
    state: "",
    assignedTo: "",
    iterationPath: "",
    priority: "",
    targetDate: "",
    description: "",
    tags: "",
  });

  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState("");

  const [editIterations, setEditIterations] = useState([]);
  const [editUsers, setEditUsers] = useState([]);
  const [editStates, setEditStates] = useState([]);

  // Initialize form when item changes
  useEffect(() => {
    if (item) {
      setEditForm({
        title: item.title || "",
        state: item.state || "",
        assignedTo: item.assignedToEmail || item.assignedTo || "",
        iterationPath: item.iterationPath || "",
        priority: item.priority !== null && item.priority !== undefined ? String(item.priority) : "",
        targetDate: item.targetDate ? item.targetDate.split("T")[0] : "",
        description: item.description || "",
        tags: item.tags || "",
      });
      setActiveEdits({ header: false, state: false, assignee: false, details: false });
    }
  }, [item]);

  // Load DevOps dropdown metadata
  useEffect(() => {
    const loadMetadata = async () => {
      if (!item) return;
      const org = item.organization || "CFPTest";
      const proj = item.project || "CFPTest";
      try {
        const [iterRes, userRes, stateRes] = await Promise.all([
          callApi(instance, accounts, "GET", `/api/assignments/iterations?organization=${org}&project=${proj}`).catch(() => null),
          callApi(instance, accounts, "GET", `/api/assignments/assignable-users?organization=${org}&project=${proj}`).catch(() => null),
          callApi(instance, accounts, "GET", `/api/assignments/workitem-states?organization=${org}&project=${proj}&type=${item.type || "User Story"}`).catch(() => null),
        ]);

        setEditIterations(iterRes?.data?.data || iterRes?.data || []);
        setEditUsers(userRes?.data?.data || userRes?.data || []);
        setEditStates(stateRes?.data?.data || stateRes?.data || []);
      } catch (err) {
        console.error("Failed to load metadata in classic view:", err);
      }
    };
    loadMetadata();
  }, [item, instance, accounts]);

  const handleStartEdit = (section) => {
    setActiveEdits((prev) => ({ ...prev, [section]: true }));
  };

  const handleSaveSection = async (section) => {
    if (!item) return;
    try {
      setEditLoading(true);
      setEditError("");
      setEditSuccess("");

      const org = item.organization || "all";
      const proj = item.project;

      const payload = {};
      if (section === "header") payload.title = editForm.title;
      if (section === "state") {
        payload.state = editForm.state;
        payload.iterationPath = editForm.iterationPath;
      }
      if (section === "assignee") {
        payload.assignedTo = editForm.assignedTo;
        if (editForm.priority) payload.priority = Number(editForm.priority);
      }
      if (section === "details") {
        payload.description = editForm.description;
        payload.tags = editForm.tags;
        payload.targetDate = editForm.targetDate || undefined;
      }

      await callApi(instance, accounts, "PATCH", `/api/assignments/task/${item.id}?organization=${org}&project=${proj}`, payload);

      setEditSuccess("Changes saved successfully!");
      setActiveEdits((prev) => ({ ...prev, [section]: false }));
      if (onSaved) onSaved();

      setTimeout(() => setEditSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      setEditError(err?.response?.data?.message || "Failed to save changes.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancelSection = (section) => {
    setActiveEdits((prev) => ({ ...prev, [section]: false }));
    setEditForm({
      title: item.title || "",
      state: item.state || "",
      assignedTo: item.assignedToEmail || item.assignedTo || "",
      iterationPath: item.iterationPath || "",
      priority: item.priority !== null && item.priority !== undefined ? String(item.priority) : "",
      targetDate: item.targetDate ? item.targetDate.split("T")[0] : "",
      description: item.description || "",
      tags: item.tags || "",
    });
  };

  const handleDownloadAttachment = async (url, name) => {
    if (!item) return;
    try {
      const response = await callApi(
        instance,
        accounts,
        "GET",
        `/api/assignments/attachment?organization=${encodeURIComponent(item.organization)}&url=${encodeURIComponent(url)}&name=${encodeURIComponent(name)}`,
        null,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/octet-stream" });
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = name;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);
    } catch (attErr) {
      console.error("Failed to download attachment", attErr);
    }
  };

  const adoLink = `https://dev.azure.com/${encodeURIComponent(item.organization || "CFPTest")}/${encodeURIComponent(item.project || "CFPTest")}/_workitems/edit/${item.id}`;

  return (
    <div style={{ minHeight: "100vh", padding: "20px", boxSizing: "border-box", background: "#f8fafc" }}>
      {/* HEADER BAR */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "12.5px", color: "#64748b", fontWeight: "500", display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px" }}>
          <span style={{ cursor: "pointer", color: "#64748b" }} onClick={onBack}>Document Index Hub</span>
          <span>&gt;</span>
          <span
            style={{ cursor: "pointer", color: "#2563eb" }}
            onClick={onBack}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            Request Pool
          </span>
          <span>&gt;</span>
          <span style={{ color: "#0f172a", fontWeight: "700" }}>View Details {item.id}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "700", color: "#0f172a" }}>Assignment Details</h1>
            {!isOnlyTeamMember && item.workItemNum && (
              <span style={{ padding: "4px 10px", borderRadius: "6px", background: "#eff6ff", color: "#1d4ed8", fontWeight: "700", fontSize: "13px", border: "1px solid #bfdbfe" }}>
                {item.workItemNum}
              </span>
            )}
          </div>
          <a
            href={adoLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 14px",
              fontSize: "12.5px",
              fontWeight: "600",
              color: "#2563eb",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "8px",
              textDecoration: "none",
            }}
          >
            <ExternalLink size={13} /> View in Azure DevOps
          </a>
        </div>
      </div>

      {/* MAIN CARD CONTAINER */}
      <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "24px", boxShadow: "0 1px 3px rgba(15,23,42,0.04)" }}>
        {/* SECTION 1: TITLE & HEADER */}
        <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: "16px", marginBottom: "20px" }}>
          {activeEdits.header ? (
            <div style={{ border: `1px solid ${THEME}`, padding: "14px", borderRadius: "10px", background: "rgba(37,99,235,0.02)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <span style={{ fontSize: "11px", fontWeight: "700", color: THEME, textTransform: "uppercase" }}>Editing Title</span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={() => handleCancelSection("header")} style={{ padding: "4px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#fff", fontSize: "11px", cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => handleSaveSection("header")} disabled={editLoading} style={{ padding: "4px 12px", borderRadius: "6px", border: "none", background: THEME, color: "#fff", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}>Save</button>
                </div>
              </div>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box" }}
              />
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#dc2626", textTransform: "uppercase", marginBottom: "4px" }}>
                  ID #{item.id}
                </div>
                <h2 style={{ margin: 0, fontSize: "19px", fontWeight: "700", color: "#0f172a" }}>{item.title}</h2>
              </div>
              <button
                onClick={() => handleStartEdit("header")}
                style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "5px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#fff", color: "#475569", fontSize: "11.5px", fontWeight: "600", cursor: "pointer" }}
              >
                <Edit size={12} /> Edit Title
              </button>
            </div>
          )}
        </div>

        {/* METADATA CARDS GRID */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "24px" }}>
          {/* Project */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "12px", display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#64748b" }}>Project</span>
            <span style={{ fontSize: "13px", color: "#334155", fontWeight: "600" }}>{item.project || "—"}</span>
          </div>

          {/* State & Sprint */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "12px", display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#64748b" }}>State & Sprint</span>
              {!activeEdits.state && (
                <button onClick={() => handleStartEdit("state")} style={{ background: "none", border: "none", color: THEME, cursor: "pointer", padding: 0 }}>
                  <Edit size={12} />
                </button>
              )}
            </div>
            {activeEdits.state ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
                <SearchableSelect
                  options={editStates.map((st) => ({ value: st, label: st }))}
                  value={editForm.state}
                  onChange={(val) => setEditForm((prev) => ({ ...prev, state: val }))}
                />
                <SearchableSelect
                  options={editIterations.map((it) => ({ value: it.path || it.name || it, label: it.name || it.path || it }))}
                  value={editForm.iterationPath}
                  onChange={(val) => setEditForm((prev) => ({ ...prev, iterationPath: val }))}
                />
                <div style={{ display: "flex", gap: "4px", marginTop: "4px" }}>
                  <button onClick={() => handleCancelSection("state")} style={{ flex: 1, padding: "3px", borderRadius: "4px", border: "1px solid #cbd5e1", background: "#fff", fontSize: "10px", cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => handleSaveSection("state")} disabled={editLoading} style={{ flex: 1, padding: "3px", borderRadius: "4px", border: "none", background: THEME, color: "#fff", fontSize: "10px", fontWeight: "700", cursor: "pointer" }}>Save</button>
                </div>
              </div>
            ) : (
              <div>
                <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: "600", background: "#eff6ff", color: "#1d4ed8" }}>
                  {item.state || "New"}
                </span>
                {item.iterationPath && (
                  <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                    <Layers size={10} /> {item.iterationPath.split("\\").pop()}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Assignee & Priority */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "12px", display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#64748b" }}>
                {!isOnlyTeamMember ? "Assignee & Priority" : "Priority"}
              </span>
              {!isOnlyTeamMember && !activeEdits.assignee && (
                <button onClick={() => handleStartEdit("assignee")} style={{ background: "none", border: "none", color: THEME, cursor: "pointer", padding: 0 }}>
                  <Edit size={12} />
                </button>
              )}
            </div>
            {!isOnlyTeamMember && activeEdits.assignee ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
                <SearchableSelect
                  options={editUsers.map((u) => ({
                    value: u.uniqueName || u.mailAddress || u.principalName,
                    label: u.displayName,
                    sub: (u.mailAddress || u.uniqueName || "").toLowerCase(),
                  }))}
                  value={editForm.assignedTo}
                  onChange={(val) => setEditForm((prev) => ({ ...prev, assignedTo: val }))}
                  showSearch={true}
                />
                <SearchableSelect
                  options={[
                    { value: "1", label: "1 — Critical" },
                    { value: "2", label: "2 — High" },
                    { value: "3", label: "3 — Medium" },
                    { value: "4", label: "4 — Low" },
                  ]}
                  value={editForm.priority}
                  onChange={(val) => setEditForm((prev) => ({ ...prev, priority: val }))}
                />
                <div style={{ display: "flex", gap: "4px", marginTop: "4px" }}>
                  <button onClick={() => handleCancelSection("assignee")} style={{ flex: 1, padding: "3px", borderRadius: "4px", border: "1px solid #cbd5e1", background: "#fff", fontSize: "10px", cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => handleSaveSection("assignee")} disabled={editLoading} style={{ flex: 1, padding: "3px", borderRadius: "4px", border: "none", background: THEME, color: "#fff", fontSize: "10px", fontWeight: "700", cursor: "pointer" }}>Save</button>
                </div>
              </div>
            ) : (
              <div>
                {!isOnlyTeamMember && (
                  <span style={{ fontSize: "13px", color: "#334155", fontWeight: "600" }}>{item.assignedTo || "Unassigned"}</span>
                )}
                {item.priority && (
                  <div style={{ fontSize: "11px", color: "#64748b", marginTop: !isOnlyTeamMember ? "4px" : "0px" }}>
                    Priority: <strong>{item.priority}</strong>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ETA Date & Tags */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "12px", display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#64748b" }}>Target ETA & Tags</span>
            <div style={{ fontSize: "12px", color: "#334155", display: "flex", alignItems: "center", gap: "6px" }}>
              <Calendar size={12} color="#64748b" />
              <span>{item.targetDate ? new Date(item.targetDate).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" }) : "Not Set"}</span>
            </div>
            {item.tags && (
              <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginTop: "4px" }}>
                {item.tags.split(";").map((t, idx) => (
                  <span key={idx} style={{ padding: "1px 6px", borderRadius: "4px", background: "#e2e8f0", fontSize: "10px", color: "#334155", fontWeight: "500" }}>
                    {t.trim()}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* TAB BAR NAVIGATION */}
        <div style={{ display: "flex", gap: "8px", borderBottom: "1px solid #e2e8f0", marginBottom: "20px" }}>
          <button
            onClick={() => setActiveTab("details")}
            style={{
              padding: "8px 16px",
              border: "none",
              borderBottom: activeTab === "details" ? `2px solid ${THEME}` : "2px solid transparent",
              background: "none",
              color: activeTab === "details" ? THEME : "#64748b",
              fontWeight: activeTab === "details" ? "700" : "500",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            Description & Details
          </button>
          <button
            onClick={() => setActiveTab("related")}
            style={{
              padding: "8px 16px",
              border: "none",
              borderBottom: activeTab === "related" ? `2px solid ${THEME}` : "2px solid transparent",
              background: "none",
              color: activeTab === "related" ? THEME : "#64748b",
              fontWeight: activeTab === "related" ? "700" : "500",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            Related Items ({item.relatedItems?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab("attachments")}
            style={{
              padding: "8px 16px",
              border: "none",
              borderBottom: activeTab === "attachments" ? `2px solid ${THEME}` : "2px solid transparent",
              background: "none",
              color: activeTab === "attachments" ? THEME : "#64748b",
              fontWeight: activeTab === "attachments" ? "700" : "500",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            Attachments ({item.attachments?.length || 0})
          </button>
        </div>

        {/* TAB 1: DETAILS */}
        {activeTab === "details" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <span style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Description</span>
              {!activeEdits.details && (
                <button
                  onClick={() => handleStartEdit("details")}
                  style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "4px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#fff", fontSize: "11.5px", cursor: "pointer", color: "#475569" }}
                >
                  <Edit size={12} /> Edit Description
                </button>
              )}
            </div>

            {activeEdits.details ? (
              <div style={{ border: `1px solid ${THEME}`, borderRadius: "10px", padding: "14px", background: "rgba(37,99,235,0.01)" }}>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={8}
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box", outline: "none", fontFamily: "inherit" }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "10px" }}>
                  <button onClick={() => handleCancelSection("details")} style={{ padding: "6px 14px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#fff", fontSize: "12px", cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => handleSaveSection("details")} disabled={editLoading} style={{ padding: "6px 16px", borderRadius: "6px", border: "none", background: THEME, color: "#fff", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}>Save</button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "16px",
                  fontSize: "13px",
                  color: "#334155",
                  lineHeight: "1.6",
                  minHeight: "120px",
                  wordBreak: "break-word",
                }}
                dangerouslySetInnerHTML={{ __html: item.description || "<em>No description provided.</em>" }}
              />
            )}
          </div>
        )}

        {/* TAB 2: RELATED ITEMS */}
        {activeTab === "related" && (
          <div>
            {item.parent && (
              <div style={{ padding: "12px", border: "1px solid #bfdbfe", borderRadius: "10px", background: "#eff6ff", marginBottom: "16px" }}>
                <span style={{ fontSize: "10px", fontWeight: "700", color: "#1d4ed8", textTransform: "uppercase" }}>Parent Work Item</span>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", marginTop: "2px" }}>#{item.parent.id} — {item.parent.title}</div>
              </div>
            )}

            {(!item.relatedItems || item.relatedItems.length === 0) ? (
              <div style={{ padding: "24px", textAlign: "center", border: "1px dashed #e2e8f0", borderRadius: "10px", color: "#94a3b8", fontSize: "13px" }}>
                No related work items found.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {item.relatedItems.map((rel) => (
                  <div key={rel.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", border: "1px solid #e2e8f0", borderRadius: "8px", background: "#ffffff" }}>
                    <div>
                      <span style={{ fontSize: "12px", fontWeight: "700", color: THEME }}>#{rel.id}</span>
                      <span style={{ fontSize: "13px", color: "#1e293b", marginLeft: "8px" }}>{rel.title}</span>
                    </div>
                    <span style={{ fontSize: "11px", padding: "2px 8px", borderRadius: "10px", background: "#f1f5f9", color: "#475569" }}>{rel.relationType || "Related"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 3: ATTACHMENTS */}
        {activeTab === "attachments" && (
          <div>
            {(!item.attachments || item.attachments.length === 0) ? (
              <div style={{ padding: "24px", textAlign: "center", border: "1px dashed #e2e8f0", borderRadius: "10px", color: "#94a3b8", fontSize: "13px" }}>
                No files attached to this work item.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "10px" }}>
                {item.attachments.map((att, idx) => (
                  <div key={att.id || idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", border: "1px solid #e2e8f0", borderRadius: "8px", background: "#ffffff" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                      <FileText size={16} color={THEME} />
                      <span style={{ fontSize: "12.5px", fontWeight: "500", color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.name || "Attachment"}</span>
                    </div>
                    <button
                      onClick={() => handleDownloadAttachment(att.url, att.name || "attachment")}
                      style={{ padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: "6px", background: "#f8fafc", color: "#475569", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px" }}
                    >
                      <Download size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STATUS ALERTS */}
        {editError && (
          <div style={{ color: "#dc2626", background: "#fef2f2", border: "1px solid #fee2e2", padding: "10px 14px", borderRadius: "8px", fontSize: "13px", marginTop: "16px" }}>
            {editError}
          </div>
        )}
        {editSuccess && (
          <div style={{ color: "#166534", background: "#f0fdf4", border: "1px solid #dcfce7", padding: "10px 14px", borderRadius: "8px", fontSize: "13px", marginTop: "16px" }}>
            {editSuccess}
          </div>
        )}
      </div>
    </div>
  );
}
